import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Upload, Download, AlertCircle, CheckCircle2, ArrowLeft, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CANONICAL_HEADER =
  "student_external_id,assessment_name,test_year,test_date,grade_at_test,overall_scale_score,overall_level,listening,speaking,reading,writing";

const SAMPLE_ROWS = [
  "STUDENT-001,NYSESLAT,2024-2025,2025-04-15,6,3.0,Transitioning,3,3,3,2",
  "STUDENT-001,WIDA ACCESS,2023-2024,2024-02-20,5,3.4,Developing+,3.5,3.0,3.5,3.2",
  "STUDENT-002,NYSESLAT,2024-2025,2025-04-15,7,2.0,Emerging,2,2,2,2",
].join("\n");

const SAMPLE_CSV = `${CANONICAL_HEADER}\n${SAMPLE_ROWS}\n`;

/**
 * Admin CSV upload UI for prior-year scores.
 *
 * Route: /admin/prior-scores/import  (district_admin / super_admin)
 *
 * Supports dry-run validation and shows a per-row acceptance / rejection
 * summary. Does not store the uploaded CSV server-side; parsed rows go
 * straight into Mongo via /api/prior-scores/import-csv.
 */
const PriorScoresImport = () => {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mosaic_prior_scores_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please choose a CSV file first.");
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("dry_run", dryRun ? "true" : "false");
      form.append("source", "csv_import");
      const { data } = await axios.post(`${API}/prior-scores/import-csv`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (dryRun) {
        toast.success(`Dry run: ${data.accepted}/${data.total_rows} rows would be imported.`);
      } else {
        toast.success(`Imported ${data.accepted} rows (${data.rejected} rejected).`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Import failed: ${detail}`);
      setResult({ error: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="prior-scores-import-page">
      <Link to="/admin/dashboard" className="inline-flex items-center text-sm text-[#002FA7] hover:underline mb-4" data-testid="admin-back-link">
        <ArrowLeft size={16} className="mr-1" /> Back to admin dashboard
      </Link>

      <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
        Import Prior-Year Scores
      </h1>
      <p className="text-gray-600 mb-8">
        Upload a CSV of your district's previous-year language proficiency assessment results (NYSESLAT, WIDA ACCESS, W-APT, NYSITELL, WIDA Screener) so MOSAIC can render each student's growth trajectory alongside current MOSAIC results.
      </p>

      {/* Template download */}
      <div className="border-2 border-gray-200 p-6 mb-8" data-testid="template-block">
        <div className="flex items-start gap-4">
          <FileSpreadsheet size={32} className="text-[#002FA7] mt-1" />
          <div className="flex-1">
            <h3 className="font-bold text-lg mb-1">1. Download the CSV template</h3>
            <p className="text-sm text-gray-600 mb-3">
              The template includes the exact column header MOSAIC requires plus three example rows. Keep the header unchanged.
            </p>
            <Button
              onClick={handleDownloadSample}
              variant="outline"
              data-testid="download-template-btn"
            >
              <Download size={16} className="mr-2" /> Download template
            </Button>
            <pre className="mt-4 bg-gray-50 border border-gray-200 p-3 text-xs overflow-x-auto rounded">
              {CANONICAL_HEADER}
            </pre>
          </div>
        </div>
      </div>

      {/* Upload */}
      <form onSubmit={handleSubmit} className="border-2 border-gray-200 p-6 mb-8" data-testid="upload-form">
        <div className="flex items-start gap-4">
          <Upload size={32} className="text-[#002FA7] mt-1" />
          <div className="flex-1">
            <h3 className="font-bold text-lg mb-1">2. Upload your filled-in CSV</h3>
            <p className="text-sm text-gray-600 mb-4">
              Each row's <code>student_external_id</code> must match a student's MOSAIC user ID. Re-uploading will replace existing records for the same (student, assessment, year) — no duplicates created.
            </p>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:border file:border-gray-300 file:bg-white file:text-sm file:font-semibold hover:file:bg-gray-50"
              data-testid="csv-file-input"
            />
            <label className="flex items-center gap-2 mt-4 text-sm" data-testid="dry-run-toggle-label">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                data-testid="dry-run-checkbox"
              />
              Dry run (validate without importing)
            </label>
            <Button type="submit" disabled={!file || submitting} className="mt-5" data-testid="submit-csv-btn">
              {submitting ? "Processing…" : dryRun ? "Validate CSV" : "Import CSV"}
            </Button>
          </div>
        </div>
      </form>

      {/* Result */}
      {result && !result.error && (
        <div className="border-2 border-gray-200 p-6" data-testid="result-block">
          <h3 className="font-bold text-lg mb-3">
            {result.dry_run ? "Dry-run Results" : "Import Results"}
          </h3>
          <div className="grid grid-cols-3 gap-4 mb-5">
            <div className="p-4 bg-gray-50 text-center" data-testid="result-total">
              <div className="text-2xl font-bold">{result.total_rows}</div>
              <div className="text-xs text-gray-500 mt-1">TOTAL ROWS</div>
            </div>
            <div className="p-4 bg-green-50 text-center" data-testid="result-accepted">
              <div className="text-2xl font-bold text-green-700">{result.accepted}</div>
              <div className="text-xs text-gray-500 mt-1">{result.dry_run ? "WOULD IMPORT" : "IMPORTED"}</div>
            </div>
            <div className="p-4 bg-red-50 text-center" data-testid="result-rejected">
              <div className="text-2xl font-bold text-red-700">{result.rejected}</div>
              <div className="text-xs text-gray-500 mt-1">REJECTED</div>
            </div>
          </div>
          {result.errors && result.errors.length > 0 && (
            <div data-testid="result-errors">
              <h4 className="font-semibold text-sm mb-2 flex items-center gap-2 text-red-700">
                <AlertCircle size={16} /> Row errors ({result.errors.length}{result.errors.length === 50 ? " shown of possibly more" : ""})
              </h4>
              <div className="border border-gray-200 max-h-64 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Row</th>
                      <th className="px-3 py-2 text-left">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((e, i) => (
                      <tr key={i} className="border-t border-gray-100" data-testid={`error-row-${i}`}>
                        <td className="px-3 py-2 font-mono">{e.row}</td>
                        <td className="px-3 py-2">{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {result.rejected === 0 && (
            <div className="flex items-center gap-2 text-green-700 text-sm" data-testid="result-all-ok">
              <CheckCircle2 size={18} /> All rows validated successfully.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PriorScoresImport;
